/* Product media gallery — thumbnails, arrows, keyboard, zoom.
 * Also listens for variant changes so picking a colour jumps to that variant's
 * image without a page load.
 */
(function () {
  'use strict';

  class Gallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.stage = this.querySelector('[data-gallery-stage]');
      if (!this.slides.length) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showById(thumb.dataset.mediaId));
      });

      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));

      this.stage.setAttribute('tabindex', '0');
      this.stage.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
      });

      if (this.dataset.zoom === 'true') this.bindZoom();

      // Swipe on touch — no library, just a threshold on pointer delta.
      let startX = null;
      this.stage.addEventListener('pointerdown', (e) => { startX = e.clientX; });
      this.stage.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 48) this.step(dx < 0 ? 1 : -1);
        startX = null;
      });

      document.addEventListener('tb:variant-change', (event) => {
        const id = event.detail && event.detail.mediaId;
        if (id) this.showById(String(id));
      });
    }

    get activeIndex() {
      return this.slides.findIndex((s) => s.dataset.active === 'true');
    }

    step(dir) {
      const next = Math.min(this.slides.length - 1, Math.max(0, this.activeIndex + dir));
      this.show(next);
    }

    showById(id) {
      const index = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (index > -1) this.show(index);
    }

    show(index) {
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.dataset.active = active ? 'true' : 'false';
        // Stop any player leaving the stage — nothing keeps playing off-screen.
        if (!active) {
          const video = slide.querySelector('video');
          if (video && !video.paused) video.pause();
          const frame = slide.querySelector('iframe');
          if (frame) {
            const holder = slide.querySelector('tb-video');
            if (holder) holder.dataset.playing = 'false';
            frame.remove();
            const host = slide.querySelector('[data-video-target]');
            if (host && holder) holder.iframe = null;
          }
        }
      });
      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
      const thumb = this.thumbs[index];
      if (thumb && thumb.scrollIntoView) {
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    bindZoom() {
      const lightbox = this.parentElement.querySelector('[data-gallery-lightbox]');
      if (!lightbox) return;
      const img = lightbox.querySelector('img');

      this.addEventListener('click', (event) => {
        const source = event.target.closest('[data-zoom-src]');
        if (!source) return;
        img.src = source.dataset.zoomSrc;
        img.alt = source.alt || '';
        lightbox.showModal();
      });

      lightbox.addEventListener('click', (event) => {
        if (event.target.closest('[data-lightbox-close]') || event.target === lightbox) lightbox.close();
      });
    }
  }

  if (!customElements.get('tb-gallery')) customElements.define('tb-gallery', Gallery);
})();
